import { lt } from 'common/PatternMatchingUtilities';
import { urlDateToDateTime } from 'common/UrlDate';
import { Slugs } from 'common/urls/Slugs';
import { DateTime } from 'luxon';
import React from 'react';
import { useDispatch } from 'react-redux';
import { RouteChildrenProps } from 'react-router';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { FaInfoCircle, FaTriangleExclamation } from 'src/shared/design-system/FontAwesome';
import { Paragraph } from 'src/shared/design-system/Text';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Routes } from 'src/shared/Routes';
import { getIdSlug, getSlug, getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { clearCartsByShefIds, setEditableOrder } from 'src/store/cart/actions';
import { LoadedCartShef } from 'src/store/cart/types';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { Colors, FontWeight } from 'web-common/src/shared/styles';
import {
  Container,
  MenuLink,
  MessageContainer,
  ProfilePhoto,
  ShefInfoContainer,
  Title,
  TitleContainer,
} from './ShefOrderBlockHeaderStyles';

const ActionButton = styled.button`
  background: none;
  text-decoration: underline;
  font-weight: ${FontWeight.SEMI_BOLD};
  border: none;
  outline: none;
  margin-top: 4px;
`;

enum ShefHeaderState {
  SHEF_SOLD_OUT = 'SHEF_SOLD_OUT',
  SHEF_AT_CAPACITY = 'SHEF_AT_CAPACITY',
  SHEF_OVER_CAPACITY = 'SHEF_OVER_CAPACITY',
  DEFAULT = 'DEFAULT',
}

interface GetShefHeaderStateParams {
  shefNumDishesAvailable: number;
  shefNumDishesRemaining: number;
}

const getShefHeaderState = (params: GetShefHeaderStateParams): ShefHeaderState =>
  // in order of precedence
  match(params)
    .with({ shefNumDishesAvailable: 0 }, () => ShefHeaderState.SHEF_SOLD_OUT)
    .with({ shefNumDishesRemaining: lt(0) }, () => ShefHeaderState.SHEF_OVER_CAPACITY)
    .with({ shefNumDishesRemaining: 0 }, () => ShefHeaderState.SHEF_AT_CAPACITY)
    .otherwise(() => ShefHeaderState.DEFAULT);
interface ShefOrderBlockHeaderProps extends GetShefHeaderStateParams {
  shef: LoadedCartShef;
  shefMenuUrl: string;
  setActiveShefDeliveryDate: (deliveryDate: DateTime) => void;
  onEditDeliveryDate: () => void;
  isEditingOrder?: boolean;
  match: RouteChildrenProps['match'];
}

export const ShefOrderBlockHeader: React.FC<ShefOrderBlockHeaderProps> = ({
  shef,
  shefMenuUrl,
  setActiveShefDeliveryDate,
  shefNumDishesAvailable,
  shefNumDishesRemaining,
  onEditDeliveryDate,
  isEditingOrder = false,
  match: matchRoute,
}) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const activeShefId = getSlug(matchRoute, Slugs.SHEF_ID);

  const [shefMenuPathname] = shefMenuUrl.split('?');
  const isOnShefMenu = location.pathname === shefMenuPathname;
  const state = getShefHeaderState({
    shefNumDishesAvailable,
    shefNumDishesRemaining,
  });
  const shouldShowMenuLink = !isOnShefMenu || activeShefId !== shef.id;

  const goToShefUrl = () => {
    if (!isOnShefMenu) history.push(shefMenuUrl);
    const deliveryDate = urlDateToDateTime(shef.deliveryDate, shef.timeZone);
    if (deliveryDate) setActiveShefDeliveryDate(deliveryDate);
  };

  const onShefMenuClick = () => {
    if (!isEditingOrder) return goToShefUrl();
    const groupOrderId = getIdSlug(matchRoute);
    history.push(
      getSluggedPath(Routes.CONSUMER_ORDER_GROUP_ORDER_EDIT, [
        {
          slug: Slugs.ID,
          value: groupOrderId,
        },
        { slug: Slugs.SHEF_ID, value: shef.id },
      ])
    );
    dispatch(setEditableOrder({ activeCartShefId: shef.id }));
  };

  const handleRemoveShefFromCart = () => {
    dispatch(clearCartsByShefIds({ shefIds: [shef.id] }));
  };

  const [bgColor, borderColor, Message] = match<ShefHeaderState, [string, string | null, (() => JSX.Element) | null]>(
    state
  )
    .with(ShefHeaderState.SHEF_SOLD_OUT, () => [
      Colors.BELL_PEPPER_LITE_30,
      Colors.RADISH,
      () => (
        <Paragraph size='16px' margin='0' color={Colors.RADISH}>
          <FaTriangleExclamation /> Shef {shef.name} has sold out for the day.
          <br />
          <ActionButton onClick={onEditDeliveryDate}>Change delivery date</ActionButton>{' '}
          <ActionButton onClick={handleRemoveShefFromCart}>Remove from cart</ActionButton>
        </Paragraph>
      ),
    ])
    .with(ShefHeaderState.SHEF_OVER_CAPACITY, () => [
      Colors.BELL_PEPPER_LITE_30,
      Colors.RADISH,
      () => (
        <Paragraph size='16px' margin='0' color={Colors.RADISH}>
          <FaTriangleExclamation /> Shef {shef.name} is close to selling out.
          <br />
          Remove {Math.abs(shefNumDishesRemaining)} of their items to continue.
        </Paragraph>
      ),
    ])
    .with(ShefHeaderState.SHEF_AT_CAPACITY, () => [
      Colors.ELDERBERRY_LITE_30,
      Colors.GRAY_DARK_30,
      () => (
        <Paragraph size='16px' margin='0' color={Colors.GRAY_DARK_30}>
          <FaInfoCircle /> Shef is at dish capacity for delivery day.
          <br />
          You cannot add any more dishes from this shef.
        </Paragraph>
      ),
    ])
    .otherwise(() => ['#f5f5f5', null, null]);

  return (
    <Container bgColor={bgColor} borderColor={borderColor}>
      <TitleContainer>
        <ShefInfoContainer>
          <ProfilePhoto>
            <DynamicImage
              className='result-image shef-result-image'
              sourceHeight={32}
              sourceWidth={32}
              height={32}
              width={32}
              src={shef.imageUrl}
              alt={`${shef.name} image`}
            />
          </ProfilePhoto>
          <Title>Shef {shef.name}</Title>
        </ShefInfoContainer>
        {shouldShowMenuLink && (
          <MenuLink type='button' onClick={onShefMenuClick}>
            {isEditingOrder ? 'Edit Items' : 'View menu'}
          </MenuLink>
        )}
      </TitleContainer>
      {Message && (
        <MessageContainer>
          <Message />
        </MessageContainer>
      )}
    </Container>
  );
};
